// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT - CreditsModal
// Modal affichant les abonnements, packs de recharge, et historique des crédits
// IMPORTANT: Les achats sont désactivés jusqu'au 1er Mai 2026
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Coins, Zap, Crown, Rocket, Star, Check, Lock, 
  TrendingUp, Calendar, ChevronRight, CreditCard, 
  Loader2, AlertCircle, Gift, Sparkles, Clock,
  MessageSquare, Image, Mic, FileText, Bot, Users, Shield
} from 'lucide-react';
import AdminCreditsPanel from './AdminCreditsPanel';
import ReferralSection from './ReferralSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour récupérer l'userId depuis différentes sources
const getUserId = () => {
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  const buyerUser = localStorage.getItem('buyer_user');
  const clientUser = localStorage.getItem('client_user');
  const userId = localStorage.getItem('userId');
  
  if (userId) return userId;
  
  if (ecosystemUser) {
    try {
      const parsed = JSON.parse(ecosystemUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  if (buyerUser) {
    try {
      const parsed = JSON.parse(buyerUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  if (clientUser) {
    try {
      const parsed = JSON.parse(clientUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  return null;
};

// Icônes par type d'action
const ACTION_ICONS = {
  chat_simple: MessageSquare,
  chat_complex: Bot,
  text_generation: FileText,
  text_generation_pro: Sparkles,
  image_generation: Image,
  image_analysis: Image,
  transcription_minute: Mic,
  product_import: FileText,
  quote_generation: FileText,
  invoice_generation: FileText,
  admin_grant: Gift,
};

// Labels français pour les actions
const ACTION_LABELS = {
  chat_simple: 'Chat simple',
  chat_complex: 'Chat avancé',
  text_generation: 'Génération texte',
  text_generation_pro: 'Génération texte Pro',
  image_generation: 'Génération image',
  image_analysis: 'Analyse image',
  transcription_minute: 'Transcription audio (1 min)',
  product_import: 'Import produit',
  quote_generation: 'Création devis',
  invoice_generation: 'Création facture',
  admin_grant: 'Crédits offerts',
};

// Configuration des plans
const PLAN_CONFIG = {
  free: {
    name: 'Gratuit',
    icon: Users,
    color: 'from-gray-400 to-gray-500',
    badge: null,
  },
  particulier: {
    name: 'Particulier',
    icon: Star,
    color: 'from-blue-500 to-indigo-600',
    badge: '9,99€/mois',
    popular: false,
  },
  pro: {
    name: 'Pro Artisan',
    icon: Rocket,
    color: 'from-emerald-500 to-teal-600',
    badge: '49,99€/mois',
    popular: true,
  },
  business: {
    name: 'Business',
    icon: Crown,
    color: 'from-amber-500 to-orange-600',
    badge: '99,99€/mois',
    popular: false,
  },
};

export default function CreditsModal({ isOpen, onClose, balance, onRefresh }) {
  const [activeTab, setActiveTab] = useState('plans'); // plans | history | tarifs
  const [plans, setPlans] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  const userId = getUserId();
  const purchaseEnabled = balance?.purchase_enabled || false;
  
  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    const ecosystemUser = localStorage.getItem('ecosystem_user');
    const clientUser = localStorage.getItem('client_user');
    
    let userRole = null;
    let userIsAdmin = false;
    
    if (ecosystemUser) {
      try {
        const parsed = JSON.parse(ecosystemUser);
        userRole = parsed.role;
        userIsAdmin = parsed.is_admin;
      } catch(e) {}
    }
    
    if (!userRole && clientUser) {
      try {
        const parsed = JSON.parse(clientUser);
        userRole = parsed.role;
        userIsAdmin = parsed.is_admin;
      } catch(e) {}
    }
    
    setIsAdmin(userRole === 'admin' || userIsAdmin === true);
  }, [isOpen]);
  const purchaseDate = balance?.purchase_enabled_date 
    ? new Date(balance.purchase_enabled_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    : '1er Mai 2026';
  
  // Récupérer les plans
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        // Plans et tarifs
        const plansRes = await fetch(`${API_URL}/api/credits/plans`);
        if (plansRes.ok) {
          const plansData = await plansRes.json();
          setPlans(plansData);
        }
        
        // Historique
        if (userId) {
          const historyRes = await fetch(`${API_URL}/api/credits/history/${userId}?limit=30`);
          if (historyRes.ok) {
            const historyData = await historyRes.json();
            setHistory(historyData.transactions || []);
          }
        }
      } catch (err) {
        console.error('Error fetching credits data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [isOpen, userId]);
  
  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1E3A5F] to-[#3B82F6] p-5 text-white relative">
            <div className="absolute top-4 right-4 flex items-center gap-2">
              {/* Bouton Admin */}
              {isAdmin && (
                <button
                  onClick={() => setShowAdminPanel(true)}
                  className="p-1.5 rounded-full bg-amber-500 hover:bg-amber-400 transition-colors"
                  title="Gérer les crédits (Admin)"
                  data-testid="admin-credits-btn"
                >
                  <Shield className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-white/20 transition-colors"
                data-testid="credits-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <Coins className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Mes Forfaits</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-3xl font-bold tabular-nums text-white">
                    {balance?.total_credits?.toLocaleString('fr-FR') || 0}
                  </span>
                  <span className="text-white/70 text-sm">crédits disponibles</span>
                </div>
              </div>
            </div>
            
            {/* Plan actuel */}
            {balance?.subscription_plan && balance.subscription_plan !== 'free' && (
              <div className="mt-3 flex items-center gap-2">
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
                  Plan {PLAN_CONFIG[balance.subscription_plan]?.name || balance.subscription_plan}
                </span>
                <span className="text-white/60 text-xs">
                  {balance.plan_info?.credits_monthly} crédits/mois inclus
                </span>
              </div>
            )}
          </div>
          
          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
            {[
              { id: 'plans', label: 'Abonnements', icon: Crown },
              { id: 'referral', label: 'Parrainage', icon: Gift },
              { id: 'tarifs', label: 'Tarifs', icon: CreditCard },
              { id: 'history', label: 'Historique', icon: Clock },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium
                  transition-colors border-b-2 -mb-[1px] min-w-[80px]
                  ${activeTab === tab.id 
                    ? 'border-[#1E3A5F] text-[#1E3A5F] bg-white' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }
                `}
                data-testid={`credits-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
              </div>
            ) : (
              <>
                {/* Tab: Plans d'abonnement */}
                {activeTab === 'plans' && (
                  <div className="space-y-4">
                    {/* Alerte désactivation */}
                    {!purchaseEnabled && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                        <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-amber-800 font-medium text-sm">Bientôt disponible</p>
                          <p className="text-amber-700 text-xs mt-1">
                            Les abonnements seront activés le <strong>{purchaseDate}</strong>. 
                            Tu peux explorer les offres dès maintenant.
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Liste des plans */}
                    <div className="grid gap-4 sm:grid-cols-3">
                      {['particulier', 'pro', 'business'].map((planKey) => {
                        const planData = plans?.plans?.[planKey];
                        const config = PLAN_CONFIG[planKey];
                        const isCurrentPlan = balance?.subscription_plan === planKey;
                        const Icon = config.icon;
                        
                        return (
                          <div
                            key={planKey}
                            className={`
                              relative rounded-xl border-2 p-4 transition-all
                              ${isCurrentPlan 
                                ? 'border-[#1E3A5F] bg-blue-50' 
                                : 'border-gray-200 hover:border-gray-300'
                              }
                              ${config.popular ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}
                            `}
                          >
                            {/* Badge populaire */}
                            {config.popular && (
                              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full">
                                Recommandé
                              </div>
                            )}
                            
                            {/* Header plan */}
                            <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${config.color} flex items-center justify-center mb-3`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            
                            <h3 className="font-bold text-gray-900">{config.name}</h3>
                            <div className="flex items-baseline gap-1 mt-1">
                              <span className="text-2xl font-bold text-gray-900">{planData?.price}€</span>
                              <span className="text-gray-500 text-sm">/mois</span>
                            </div>
                            
                            {/* Crédits inclus */}
                            <div className="mt-3 flex items-center gap-2 text-sm">
                              <Coins className="w-4 h-4 text-amber-500" />
                              <span className="font-medium">{planData?.credits_monthly?.toLocaleString('fr-FR')} crédits/mois</span>
                            </div>
                            
                            {/* Features */}
                            <ul className="mt-3 space-y-1.5">
                              {planData?.features?.slice(0, 4).map((feature, idx) => (
                                <li key={`${planKey}-feature-${idx}`} className="flex items-start gap-2 text-xs text-gray-600">
                                  <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                            
                            {/* Bouton */}
                            <button
                              disabled={!purchaseEnabled || isCurrentPlan}
                              className={`
                                w-full mt-4 py-2 rounded-lg text-sm font-medium
                                transition-all flex items-center justify-center gap-2
                                ${isCurrentPlan
                                  ? 'bg-[#1E3A5F] text-white cursor-default'
                                  : !purchaseEnabled
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : `bg-gradient-to-r ${config.color} text-white hover:shadow-lg`
                                }
                              `}
                              data-testid={`plan-${planKey}-btn`}
                            >
                              {isCurrentPlan ? (
                                <>
                                  <Check className="w-4 h-4" />
                                  Plan actuel
                                </>
                              ) : !purchaseEnabled ? (
                                <>
                                  <Lock className="w-4 h-4" />
                                  Disponible le 1er Mai
                                </>
                              ) : (
                                <>
                                  <CreditCard className="w-4 h-4" />
                                  Choisir
                                </>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Tab: Parrainage */}
                {activeTab === 'referral' && (
                  <ReferralSection />
                )}
                
                {/* Tab: Tarifs des actions */}
                {activeTab === 'tarifs' && (
                  <div className="space-y-4">
                    <p className="text-gray-600 text-sm">
                      Chaque action IA consomme des crédits. Voici le coût de chaque action :
                    </p>
                    
                    <div className="bg-gray-50 rounded-xl divide-y divide-gray-200">
                      {Object.entries(plans?.credit_costs || {}).map(([action, cost]) => {
                        const Icon = ACTION_ICONS[action] || Zap;
                        const label = ACTION_LABELS[action] || action;
                        
                        return (
                          <div key={action} className="flex items-center justify-between p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
                                <Icon className="w-4 h-4 text-gray-600" />
                              </div>
                              <span className="text-sm font-medium text-gray-700">{label}</span>
                            </div>
                            <div className="flex items-center gap-1 text-amber-600 font-semibold">
                              <Coins className="w-4 h-4" />
                              <span>{cost}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
                      <p className="font-medium mb-1">Transparence totale</p>
                      <p className="text-blue-700 text-xs">
                        1 crédit = environ 0,01€. Tu sais exactement ce que tu consommes.
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Tab: Historique */}
                {activeTab === 'history' && (
                  <div className="space-y-3">
                    {history.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="font-medium">Aucune transaction</p>
                        <p className="text-sm text-gray-400 mt-1">Ton historique apparaîtra ici</p>
                      </div>
                    ) : (
                      history.map((tx) => {
                        const Icon = ACTION_ICONS[tx.action] || Zap;
                        const label = tx.description || ACTION_LABELS[tx.action] || tx.action;
                        const isPositive = tx.amount > 0;
                        const date = new Date(tx.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                        
                        return (
                          <div 
                            key={tx.id} 
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`
                                w-8 h-8 rounded-lg flex items-center justify-center
                                ${isPositive ? 'bg-emerald-100' : 'bg-gray-200'}
                              `}>
                                <Icon className={`w-4 h-4 ${isPositive ? 'text-emerald-600' : 'text-gray-600'}`} />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800 line-clamp-1">{label}</p>
                                <p className="text-xs text-gray-500">{date}</p>
                              </div>
                            </div>
                            <div className={`font-semibold ${isPositive ? 'text-emerald-600' : 'text-gray-600'}`}>
                              {isPositive ? '+' : ''}{tx.amount}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
      
      {/* Panel Admin pour octroyer des crédits */}
      <AdminCreditsPanel 
        isOpen={showAdminPanel} 
        onClose={() => setShowAdminPanel(false)} 
      />
    </AnimatePresence>,
    document.body
  );
}
